import { useState } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  MessageSquare,
  Pencil,
  Trash2,
  Loader2,
  ShieldX,
  Send,
  X,
  Flag,
  CornerDownRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API = `${getApiUrl()}/api`;

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

// Render @mentions and bare URLs inside plain comment text.
function renderRich(text) {
  if (!text) return null;
  const parts = [];
  const regex = /(https?:\/\/[^\s]+)|(@[A-Za-z0-9_\-]{3,20})/g;
  let last = 0, m, key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      parts.push(
        <a key={key++} href={m[1]} target="_blank" rel="noopener noreferrer"
           onClick={(e) => e.stopPropagation()}
           className="text-purple-400 hover:underline break-all">{m[1]}</a>
      );
    } else {
      parts.push(<span key={key++} className="text-purple-400 font-medium">{m[2]}</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function NewsComments({ updateId, initialCount = 0, user }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [postingReply, setPostingReply] = useState(false);
  const [adminTarget, setAdminTarget] = useState(null);
  const [adminReason, setAdminReason] = useState("");
  const [adminSaving, setAdminSaving] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportSaving, setReportSaving] = useState(false);

  const isAdmin = !!user?.isAdmin;
  const myId = user?.user_id;

  const loadComments = async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/community/updates/${updateId}/comments?page=${p}`);
      setComments(data.comments || []);
      setPage(data.page || 1);
      setPages(data.pages || 1);
    } catch (e) {
      console.error("Failed to load comments:", e);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (e) => {
    e?.stopPropagation?.();
    const next = !open;
    setOpen(next);
    if (next && comments.length === 0) loadComments(1);
  };

  const handlePost = async () => {
    const content = newText.trim();
    if (!content) return;
    setPosting(true);
    try {
      await axios.post(`${API}/community/updates/${updateId}/comments`, { content });
      setNewText("");
      setCount((c) => c + 1);
      // Reload last page so the new top-level comment is visible.
      await loadComments(pages);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (parentId) => {
    const content = replyText.trim();
    if (!content) return;
    setPostingReply(true);
    try {
      await axios.post(`${API}/community/updates/${updateId}/comments`, { content, parentId });
      setReplyText("");
      setReplyingTo(null);
      setCount((c) => c + 1);
      await loadComments(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post reply");
    } finally {
      setPostingReply(false);
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditText(c.content); };

  const saveEdit = async (id) => {
    const content = editText.trim();
    if (!content) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/community/comments/${id}`, { content });
      setEditingId(null);
      setEditText("");
      await loadComments(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update comment");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (c) => {
    try {
      await axios.delete(`${API}/community/comments/${c.id}`);
      const removed = 1 + (c.replies ? c.replies.length : 0);
      setCount((n) => Math.max(0, n - removed));
      await loadComments(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete comment");
    }
  };

  const confirmAdminDelete = async () => {
    if (!adminTarget) return;
    setAdminSaving(true);
    try {
      await axios.post(`${API}/community/comments/${adminTarget.id}/admin-delete`, {
        reason: adminReason.trim(),
      });
      setAdminTarget(null);
      setAdminReason("");
      await loadComments(page);
      toast.success("Comment removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove comment");
    } finally {
      setAdminSaving(false);
    }
  };

  const confirmReport = async () => {
    if (!reportTarget) return;
    setReportSaving(true);
    try {
      await axios.post(`${API}/community/comments/${reportTarget.id}/report`, {
        reason: reportReason.trim(),
      });
      setReportTarget(null);
      setReportReason("");
      toast.success("Reported to moderators");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to report comment");
    } finally {
      setReportSaving(false);
    }
  };

  const renderComment = (c, isReply, parentName) => {
    const mine = c.userId === myId;
    const isEditing = editingId === c.id;
    return (
      <div
        key={c.id}
        className={`rounded-md border border-white/5 bg-black/30 p-2.5 ${isReply ? "ml-5 mt-2 border-l-2 border-l-purple-500/20" : ""}`}
        data-testid={`news-comment-${c.id}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {isReply && <CornerDownRight className="w-3 h-3 text-white/25 shrink-0" />}
            <span className="text-xs font-semibold text-white/80 truncate">{c.userName}</span>
            <span className="text-[10px] text-white/30 shrink-0">{formatTime(c.createdAt)}</span>
            {c.edited && !c.deletedByAdmin && (
              <span className="text-[10px] text-white/25 italic shrink-0">(edited)</span>
            )}
          </div>
          {!c.deletedByAdmin && !isEditing && (
            <div className="flex items-center gap-1 shrink-0">
              {!isReply && (
                <button
                  onClick={() => { setReplyingTo(c.id); setReplyText(""); }}
                  className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-purple-300 transition-colors"
                  title="Reply" data-testid={`news-comment-reply-${c.id}`}
                >
                  <CornerDownRight className="w-3 h-3" />
                </button>
              )}
              {mine && (
                <>
                  <button onClick={() => startEdit(c)}
                    className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors"
                    title="Edit" data-testid={`news-comment-edit-${c.id}`}>
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => handleDelete(c)}
                    className="p-1 rounded hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors"
                    title="Delete" data-testid={`news-comment-delete-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
              {!mine && (
                <button onClick={() => { setReportTarget(c); setReportReason(""); }}
                  className="p-1 rounded hover:bg-orange-500/20 text-white/40 hover:text-orange-400 transition-colors"
                  title="Report to moderators" data-testid={`news-comment-report-${c.id}`}>
                  <Flag className="w-3 h-3" />
                </button>
              )}
              {isAdmin && c.reportCount > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 text-[10px] font-semibold flex items-center gap-0.5"
                  title={`${c.reportCount} open report${c.reportCount !== 1 ? "s" : ""}`}
                  data-testid={`news-comment-reportbadge-${c.id}`}>
                  <Flag className="w-2.5 h-2.5" />{c.reportCount}
                </span>
              )}
              {isAdmin && (
                <button onClick={() => { setAdminTarget(c); setAdminReason(""); }}
                  className="p-1 rounded hover:bg-amber-500/20 text-white/40 hover:text-amber-400 transition-colors"
                  title="Remove as admin" data-testid={`news-comment-admin-delete-${c.id}`}>
                  <ShieldX className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>

        {isReply && parentName && (
          <p className="text-[10px] text-white/30 mt-0.5 flex items-center gap-1" data-testid={`news-comment-replytag-${c.id}`}>
            <CornerDownRight className="w-2.5 h-2.5 shrink-0" />
            replying to <span className="text-purple-400 font-medium">@{parentName}</span>
          </p>
        )}

        {c.deletedByAdmin ? (
          <p className="text-xs text-red-400/70 italic mt-1" data-testid={`news-comment-tombstone-${c.id}`}>
            post deleted by admin per "{c.deletedReason}" rule
          </p>
        ) : isEditing ? (
          <div className="mt-2 space-y-2">
            <Textarea value={editText} onChange={(e) => setEditText(e.target.value)}
              className="bg-black/50 border-white/10 text-sm min-h-[60px]"
              data-testid={`news-comment-edit-input-${c.id}`} />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => saveEdit(c.id)} disabled={savingEdit || !editText.trim()}
                className="h-7 text-xs bg-purple-600 hover:bg-purple-700" data-testid={`news-comment-save-${c.id}`}>
                {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditText(""); }} className="h-7 text-xs">
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-white/70 mt-1 whitespace-pre-wrap break-words">{renderRich(c.content)}</p>
        )}

        {/* Reply composer (top-level only) */}
        {replyingTo === c.id && !isReply && (
          <div className="mt-2 flex items-start gap-2">
            <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a reply..." className="bg-black/50 border-white/10 text-sm min-h-[40px] flex-1"
              data-testid={`news-reply-input-${c.id}`} />
            <div className="flex flex-col gap-1">
              <Button size="sm" onClick={() => handleReply(c.id)} disabled={postingReply || !replyText.trim()}
                className="h-7 bg-purple-600 hover:bg-purple-700" data-testid={`news-reply-post-${c.id}`}>
                {postingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(""); }} className="h-7 text-xs px-2">
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Replies */}
        {!isReply && Array.isArray(c.replies) && c.replies.map((r) => renderComment(r, true, c.userName))}
      </div>
    );
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/10" onClick={(e) => e.stopPropagation()}>
      <button onClick={toggle}
        className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
        data-testid={`news-comments-toggle-${updateId}`}>
        <MessageSquare className="w-3.5 h-3.5" />
        {count > 0 ? `${count} Comment${count !== 1 ? "s" : ""}` : "Comment"}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`news-comments-panel-${updateId}`}>
          {loading ? (
            <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-white/30" /></div>
          ) : (
            <div className="space-y-2">
              {comments.length === 0 && (
                <p className="text-white/30 text-xs italic">No comments yet. Be the first to post.</p>
              )}
              {comments.map((c) => renderComment(c, false))}

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-1" data-testid={`news-comments-pager-${updateId}`}>
                  <button onClick={() => loadComments(page - 1)} disabled={page <= 1}
                    className="p-1 rounded hover:bg-white/10 text-white/50 disabled:opacity-30"
                    data-testid={`news-comments-prev-${updateId}`}>
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-white/40">Page {page} of {pages}</span>
                  <button onClick={() => loadComments(page + 1)} disabled={page >= pages}
                    className="p-1 rounded hover:bg-white/10 text-white/50 disabled:opacity-30"
                    data-testid={`news-comments-next-${updateId}`}>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Composer */}
          <div className="flex items-start gap-2">
            <Textarea value={newText} onChange={(e) => setNewText(e.target.value)}
              placeholder="Write a comment... use @username to mention someone"
              className="bg-black/50 border-white/10 text-sm min-h-[44px] flex-1"
              data-testid={`news-comment-input-${updateId}`} />
            <Button onClick={handlePost} disabled={posting || !newText.trim()}
              className="bg-purple-600 hover:bg-purple-700 shrink-0" data-testid={`news-comment-post-${updateId}`}>
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Admin remove-with-reason dialog */}
      <Dialog open={!!adminTarget} onOpenChange={(v) => { if (!v) setAdminTarget(null); }}>
        <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10 text-white" data-testid="news-comment-admin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldX className="w-5 h-5 text-amber-400" /> Remove Comment
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              The comment stays in place showing: <em>post deleted by admin per "&lt;rule&gt;" rule</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-1.5">
            <label className="text-xs text-white/60">Rule / reason</label>
            <Input value={adminReason} onChange={(e) => setAdminReason(e.target.value)}
              placeholder="e.g., No spam, Be respectful" className="bg-black/50 border-white/10"
              data-testid="news-comment-admin-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdminTarget(null)}>Cancel</Button>
            <Button onClick={confirmAdminDelete} disabled={adminSaving}
              className="bg-amber-600 hover:bg-amber-700" data-testid="news-comment-admin-confirm">
              {adminSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Remove Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report-to-moderators dialog */}
      <Dialog open={!!reportTarget} onOpenChange={(v) => { if (!v) setReportTarget(null); }}>
        <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10 text-white" data-testid="news-comment-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-orange-400" /> Report Comment
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              Flag this comment for moderator review. Tell us what's wrong with it.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-1.5">
            <label className="text-xs text-white/60">Reason</label>
            <Input value={reportReason} onChange={(e) => setReportReason(e.target.value)}
              placeholder="e.g., Spam, Harassment, Off-topic" className="bg-black/50 border-white/10"
              data-testid="news-comment-report-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportTarget(null)}>Cancel</Button>
            <Button onClick={confirmReport} disabled={reportSaving}
              className="bg-orange-600 hover:bg-orange-700" data-testid="news-comment-report-confirm">
              {reportSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
